import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'

import Loader from '@/modules/base/components/loader/loader'
import ButtonIconSquare from '@/modules/base/components/buttons/icon-square/button-icon-square'
import CancelButton from '@/modules/base/components/buttons/cancel-button/cancel-button'
import { ModalBig } from '@/modules/base/components/modal/index'
import { Checkbox } from '@/modules/base/components/inputs'

import IconCheck from '@assets/images/icons/check.svg'

const useStyle = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '100%',
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  abortButton: {
    marginRight: '15px',
  },
  topbuttons: {
    display: 'flex',
    justifyContent: 'space-between',
    '& a': {
      fontSize: '12px',
      fontWeight: 500,
    },
  },
  disabled: {
    color: theme.disabledColor,
    cursor: 'initial',
    '&:hover': {
      color: theme.disabledColor,
      cursor: 'initial',
    },
  },
  loader: {
    height: '100%',
  },
  checkboxLabel: {
    paddingLeft: '10px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  items: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)',
    gridGap: '25px',
    marginBottom: '10px',
  },
  defaultCols: {
    '& h3': {
      fontSize: '14px',
      fontWeight: '500',
      color: theme.text,
      margin: '30px 0 20px 0',
    },
  },
  addtionalCols: {
    '& h3': {
      fontSize: '14px',
      fontWeight: '500',
      color: theme.text,
      margin: '30px 0 20px 0',
    },
  },
  autoSizeColumns: {
    marginTop: 20,
    '& span': {
      paddingLeft: '10px',
    },
  },
}))

const FILTERED_COLS = ['menu', 'select']

const ColumnSettingModal = (props) => {
  const classes = useStyle()
  const {
    open = false,
    isLoading,
    updatedColumns = [],
    autoSizeColumns: autoSizeColumnsProps = true,
    hideAutoSizeColumns,
    onCancel,
    onSave,
  } = props

  const [state, setState] = useState({
    updatedColumns,
    allSelected: false,
    allDefaultSelected: false,
  })

  const [autoSizeColumns, setAutoSizeColumns] = useState(autoSizeColumnsProps)

  useEffect(() => {
    setState({
      ...state,
      updatedColumns,
    })
  }, [updatedColumns])

  useEffect(() => {
    setAutoSizeColumns(autoSizeColumns)
  }, [autoSizeColumnsProps])

  const filteredCols = useMemo(() => {
    if (!state.updatedColumns) { return [] }
    return state.updatedColumns.filter((col) => {
      return !col.internal
      && col.displayed
      && !FILTERED_COLS.includes(col.colId)
    })
  }, [state.updatedColumns])

  useEffect(() => {
    const allSelected = !filteredCols?.some(col => !col.active)
    const allDefaultSelected = !filteredCols?.some(col => col.active !== col.default)
    if (state.allSelected !== allSelected || state.allDefaultSelected !== allDefaultSelected) {
      setState({
        ...state,
        allSelected,
        allDefaultSelected,
      })
    }
  }, [state])

  const handleCheckboxChange = useCallback((name) => {
    return (checked) => {
      setState(prevState => ({
        ...prevState,
        updatedColumns: prevState.updatedColumns?.map(col => col.name !== name ? col : showCol(col, checked)),
      }))
    }
  }, [])

  const toggleAll = () => {
    setState({
      ...state,
      updatedColumns: state.updatedColumns?.map(col => showCol(col, !state.allSelected)),
    })
  }

  const setDefault = () => {
    if (!state.allDefaultSelected) {
      setState({
        ...state,
        updatedColumns: state.updatedColumns?.map(col => showCol(col, col.default)),
      })
    }
  }

  const handleAutoSizeColumnsChange = (checked) => {
    if (hideAutoSizeColumns) { return }
    setAutoSizeColumns(checked)
  }

  const showCol = (col, show) => {
    const alwaysShow = FILTERED_COLS.includes(col.colId) || col.required
    const active = !col.internal && (alwaysShow || show)
    return { ...col, active, hide: !active }
  }

  const save = () => {
    onSave(state.updatedColumns, autoSizeColumns)
  }

  const columns = useMemo(() => {
    const initialColumns = {
      default: [],
      other: [],
      custom: [],
    }
    if (!filteredCols?.length) { return initialColumns }

    return filteredCols.reduce((result, column, index) => {
      if (column.internal || !column.displayed || FILTERED_COLS.includes(column.colId)) {
        return result
      }

      let type = 'other'
      if (column.default) {
        type = 'default'
      } else if (column.name.startsWith('customField_')) {
        type = 'custom'
      }

      result[type].push((
        <Checkbox
          key={`${type}-${index}`}
          disabled={column.required}
          checked={column.active}
          onChange={handleCheckboxChange(column.name)}
          name={column.name}
          className={classes.checkbox}
          dataQsId={`columnSettingModal-${column.name}`}
        >
          <span title={column.label} className={classes.checkboxLabel}>
            {column.label}
          </span>
        </Checkbox>
      ))

      return result
    }, initialColumns)
  }, [filteredCols, handleCheckboxChange])

  const defaultColumns = useMemo(() => {
    if (!columns.default.length) { return null }
    return (
      <div className={classes.defaultCols}>
        <h3>{lang.columnsSetting.default}</h3>
        <div className={classes.items}>
          {columns.default}
        </div>
      </div>
    )
  }, [columns.default])
  const otherColumns = useMemo(() => {
    if (!columns.other.length) { return null }
    return (
      <div className={classes.addtionalCols}>
        <h3>{lang.columnsSetting.additional}</h3>
        <div className={classes.items}>
          {columns.other}
        </div>
      </div>
    )
  }, [columns.other])
  const customColumns = useMemo(() => {
    if (!columns.custom.length) { return null }
    return (
      <div className={classes.addtionalCols}>
        <h3>{lang.columnsSetting.custom}</h3>
        <div className={classes.items}>
          {columns.custom}
        </div>
      </div>
    )
  }, [columns.custom])

  const loader = isLoading && <Loader text={lang.columnsSetting.saving} customClass={classes.loader} />
  const actions = [
    <CancelButton
      dataQsId='columnSettingModal-cancel'
      key='cancel'
      label={lang.cancelButtonLabel}
      customClass={classes.abortButton}
      disabled={isLoading}
      onClick={onCancel} />,
    <ButtonIconSquare
      dataQsId='columnSettingModal-save'
      key='save'
      label={lang.saveButtonLabel}
      icon={IconCheck}
      disabled={isLoading}
      onClick={save} />,
  ]

  return (
    <ModalBig
      open={open}
      actions={actions}
      modalspace={40}
      width={680}
      paddingtop={0}
      title={lang.columnsSetting.adjust}
      onClose={onCancel}
    >
      {loader}
      <div className={classes.content}>
        <div className={classes.container}>
          <div className={classes.topbuttons}>
            <a onClick={toggleAll}>
              {state.allSelected ? lang.columnsSetting.deselectAll : lang.columnsSetting.selectAll}
            </a>
            <a className={state.allDefaultSelected ? classes.disabled : ''} onClick={setDefault}>{lang.columnsSetting.setDefault}</a>
          </div>
          {!hideAutoSizeColumns && (
            <Checkbox
              checked={autoSizeColumns}
              customClass={classes.autoSizeColumns}
              onChange={handleAutoSizeColumnsChange}
              name='autoSizeColumns'
              dataQsId='columnSettingModal-autoSizeColumns'
            >
              <span>{lang.columnsSetting.autoSizeColumns}</span>
            </Checkbox>
          )}
          {defaultColumns}
          {otherColumns}
          {customColumns}
        </div>
      </div>
    </ModalBig>
  )
}

ColumnSettingModal.propTypes = {
  open: PropTypes.bool,
  isLoading: PropTypes.bool,
  updatedColumns: PropTypes.array,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  autoSizeColumns: PropTypes.bool,
  hideAutoSizeColumns: PropTypes.bool,
}

export default ColumnSettingModal
